import { twm } from '#utils/twm.ts'

export type GapVals = 'px' | '2' | '4' | '6' | '8' | '10' | '12' | '14'
// biome-ignore format: keep the formatting
type PaddingVals =
  '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | 'px'
type MarginVals = PaddingVals | 'auto'
// biome-ignore format: keep the formatting
type MaxWidthVals =
  '0' | '16' | '20' | '24' | '28' | '32' | '36' | '40' | '44' | '48' | '52' |
  '56' | '60' | '64' | '72' | '80' | '96' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' |
  '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' | 'px' | 'full' | 'min' |
  'max' | 'fit' | 'prose' | 'none'
type TextVals = '2xs' | 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | 'center'
type LeadingVals = 'none' | 'tight' | 'snug' | 'normal' | 'relaxed' | 'loose'
type FontWeightVals = 'normal' | 'semibold' | 'bold' | 'extrabold'
type FontFamilyVals = 'sans' | 'mono'
type FontVals = FontFamilyVals | FontWeightVals
type BorderVals = 'dotted-2'
// biome-ignore format: keep the formatting
type ColorVals =
  | 'base-1' | 'base-2' | 'base-3' | 'base-4' | 'base-5' | 'base-6' | 'base-7' | 'base-8' | 'base-9'
  | 'pop-1'  | 'pop-2'  | 'pop-3'  | 'pop-4'
  | 'warn-1' | 'warn-2' | 'warn-3' | 'warn-4'
  | 'flag-1' | 'flag-2' | 'flag-3' | 'flag-4' | 'flag-5'
  | 'ok-1'   | 'ok-2'   | 'ok-3'   | 'ok-4'   | 'ok-5'   | 'ok-6'
type WhitespaceVals = 'pre-line' | 'none'

export type Margins =
  | `m-${MarginVals}`
  | `mx-${MarginVals}`
  | `my-${MarginVals}`
  | `mt-${MarginVals}`
  | `mr-${MarginVals}`
  | `mb-${MarginVals}`
  | `ml-${MarginVals}`
  | `last:m-${MarginVals}`
  | `last:mx-${MarginVals}`
  | `last:my-${MarginVals}`
  | `last:mt-${MarginVals}`
  | `last:mr-${MarginVals}`
  | `last:mb-${MarginVals}`
  | `last:ml-${MarginVals}`
export type Paddings =
  | `p-${PaddingVals}`
  | `px-${PaddingVals}`
  | `py-${PaddingVals}`
  | `pt-${PaddingVals}`
  | `pr-${PaddingVals}`
  | `pb-${PaddingVals}`
  | `pl-${PaddingVals}`
export type Gaps = `gap-${GapVals}`
export type MaxWidths = `max-w-${MaxWidthVals}`
type TextSizes = `text-${TextVals}`
type Leadings = `leading-${LeadingVals}`
type Fonts = `font-${FontVals}`
type Borders = `border-b-${BorderVals}` | `border-t-${BorderVals}`
type Truncate = 'truncate'
type TextColor = `text-${ColorVals}`
type Display = 'block' | 'inline' | 'inline-block' | 'flex'
type Whitespace = `whitespace-${WhitespaceVals}`
type FlexChild = 'order-last' | 'order-first'

export type TextClasses =
  | Borders
  | Display
  | FlexChild
  | Fonts
  | Leadings
  | Margins
  | Paddings
  | TextColor
  | TextSizes
  | Truncate
  | Whitespace
  | 'tabular-nums'
  | 'break-all'
  | '_'

/**
 * This shenanigan allows us to make a function which only accepts a defined set
 * of strings, in any order. A subset of Tailwind classes in this case.
 *
 * @see {@link getClassFn}
 * @see {@link https://stackoverflow.com/a/70613449/976972}
 */
type ValidClass<T extends string, U extends string> = T extends U
  ? T
  : T extends `${U} ${infer R}`
    ? T extends `${infer F} ${R}`
      ? `${F} ${ValidClass<R, Exclude<U, F>>}`
      : never
    : U

/**
 * Curried function which is first populated with the wanted type.
 *
 * @example
 * ```ts
 * const getSpacingClasses = getClassFn<Margins | Paddings>()
 *
 * // Only accepts margin and padding classes
 * const classNames = getSpacingClasses('m-0 p-0')
 * ```
 */
export const getClassFn = <U extends string>() => {
  return <T extends string>(
    className: T extends ValidClass<T, U> ? T : ValidClass<T, U>
  ) => className
}

export const getHatchedClassFn = <U extends string>() => {
  return <T extends string>(
    className: T extends ValidClass<T, U> ? T : ValidClass<T, U>,
    classNameFn?: ((arg: any) => string) | string
  ) => {
    const classFn = getClassFn<U>()
    const extraClasses =
      typeof classNameFn === 'function' ? classNameFn(classFn) : classNameFn
    return twm(className, extraClasses)
  }
}
